import {
  calculatePets as calculatePetsApi,
  checkOtp as checkOtpApi,
  checkPayment as checkPaymentApi,
  confirmPetsContract as confirmPetsContractApi,
  fetchOrder as fetchOrderApi,
  savePetsOrder as savePetsOrderApi,
  sendOtp as sendOtpApi,
  takePetsContract as takePetsContractApi,
  tariffsPets as tariffsPetsApi,
} from '@/entities/insurance/api';
import {
  confirmContractResults,
  invoiceStatuses,
  saveOrderResults,
} from '@/entities/insurance/config';
import { Env } from '@/shared/config/environment';
import { createPersistence } from '@/shared/lib/store';

const BACKEND_ERRORS = Object.freeze({
  BLOCKED_ORDERS: 'Заблокований для ордерів',
  BLACKLIST: 'CompanyClientBlackList block',
  INSUFFICIENT_FUNDS: 'недостатньо коштів',
});

const { createAccessor, defineMutation } = createPersistence('pets-');
const storage = createAccessor();

const state = {
  pet: {
    petType: storage.get('pet-type', null),
    petBreed: storage.get('pet-breed', null),
    petSex: storage.get('pet-sex', null),
    petAlias: storage.get('pet-alias', null),
    petBirthDate: storage.get('pet-birth-date', null),
  },
  vetPassport: {
    serial: storage.get('vet-passport-serial', null),
    number: storage.get('vet-passport-number', null),
    date: storage.get('vet-passport-date', null),
    issuedBy: storage.get('vet-passport-issued-by', null),
  },
  microchip: {
    code: storage.get('microchip-code', null),
    date: storage.get('microchip-date', null),
  },
  policyholder: {
    id: storage.get('policyholder-id', null),
    type: storage.get('policyholder-type', 'natural'),
    fullName: null,
    taxNumber: null,
    withoutTaxNumber: storage.get('policyholder-without-tax-number', false),
    address: null,
    email: null,
    phone: null,
    dateOfBirth: null,
    documentsList: [],
    identificationCode: null,
  },
  document: {
    id: storage.get('document-id', null),
    type: null,
    serialNumber: null,
    number: null,
    issuingAuthority: null,
    issueDate: null,
    expirationDate: null,
  },
  contract: {
    startDate: storage.get('contract-start-date', null),
    estimatedPeriod: storage.get('contract-estimated-period', null),
  },
  calculator: {
    earnings: storage.get('calculator-earnings', null),
  },
  offer: storage.get('offer', null),
  order: {
    id: storage.get('order-id', null),
    statusId: storage.get('order-status-id', null),
    isPriceApproved: storage.get('is-price-approved', false),
    payments: [],
    mtsbuCode: null,
    mtsbuLink: storage.get('mtsbu-link', null),
    paymentInvoiceId: storage.get('payment-invoice-id', null),
    paymentSystemCode: storage.get('payment-system-code', null),
  },
  otpFlags: {
    requiresOtp: storage.get('otp-requires', false),
    otpType: storage.get('otp-type', null),
    otpSent: storage.get('otp-sent', false),
    otpVerified: storage.get('otp-verified', false),
  },
  sanctions: {
    isSanctioned: false,
    sanctionMessage: null,
    status: null,
  },
};

const mutations = {
  SET_PET_TYPE: defineMutation('pet.petType', 'pet-type', null),
  SET_PET_BREED: defineMutation('pet.petBreed', 'pet-breed', null),
  SET_PET_SEX: defineMutation('pet.petSex', 'pet-sex', null),
  SET_PET_ALIAS: defineMutation('pet.petAlias', 'pet-alias', null),
  SET_PET_BIRTH_DATE: defineMutation('pet.petBirthDate', 'pet-birth-date', null),

  SET_VET_PASSPORT_SERIAL: defineMutation('vetPassport.serial', 'vet-passport-serial', null),
  SET_VET_PASSPORT_NUMBER: defineMutation('vetPassport.number', 'vet-passport-number', null),
  SET_VET_PASSPORT_DATE: defineMutation('vetPassport.date', 'vet-passport-date', null),
  SET_VET_PASSPORT_ISSUED_BY: defineMutation('vetPassport.issuedBy', 'vet-passport-issued-by', null),

  SET_MICROCHIP_CODE: defineMutation('microchip.code', 'microchip-code', null),
  SET_MICROCHIP_DATE: defineMutation('microchip.date', 'microchip-date', null),

  SET_POLICYHOLDER_ID: defineMutation('policyholder.id', 'policyholder-id', null),
  SET_POLICYHOLDER_TYPE: defineMutation('policyholder.type', 'policyholder-type', 'natural'),
  SET_POLICYHOLDER_FULL_NAME(state, fullName) {
    state.policyholder.fullName = fullName;
  },
  SET_POLICYHOLDER_TAX_NUMBER(state, taxNumber) {
    state.policyholder.taxNumber = taxNumber;
  },
  SET_POLICYHOLDER_WITHOUT_TAX_NUMBER: defineMutation('policyholder.withoutTaxNumber', 'policyholder-without-tax-number', false),
  SET_POLICYHOLDER_ADDRESS(state, address) {
    state.policyholder.address = address;
  },
  SET_POLICYHOLDER_EMAIL(state, email) {
    state.policyholder.email = email;
  },
  SET_POLICYHOLDER_PHONE(state, phone) {
    state.policyholder.phone = phone;
  },
  SET_POLICYHOLDER_DATE_OF_BIRTH(state, dateOfBirth) {
    state.policyholder.dateOfBirth = dateOfBirth;
  },
  SET_POLICYHOLDER_DOCUMENTS_LIST(state, documentsList) {
    if (documentsList === null) return;
    if (Array.isArray(documentsList)) {
      state.policyholder.documentsList = [...documentsList];
    }
  },

  SET_DOCUMENT_ID: defineMutation('document.id', 'document-id', null),
  SET_DOCUMENT_TYPE(state, documentType) {
    state.document.type = documentType;
  },
  SET_DOCUMENT_SERIAL_NUMBER(state, serialNumber) {
    state.document.serialNumber = serialNumber;
  },
  SET_DOCUMENT_NUMBER(state, number) {
    state.document.number = number;
  },
  SET_DOCUMENT_ISSUING_AUTHORITY(state, issuingAuthority) {
    state.document.issuingAuthority = issuingAuthority;
  },
  SET_DOCUMENT_ISSUE_DATE(state, issueDate) {
    state.document.issueDate = issueDate;
  },
  SET_DOCUMENT_EXPIRATION_DATE(state, expirationDate) {
    state.document.expirationDate = expirationDate;
  },

  SET_CONTRACT_START_DATE: defineMutation('contract.startDate', 'contract-start-date', null),
  SET_CONTRACT_ESTIMATED_PERIOD: defineMutation('contract.estimatedPeriod', 'contract-estimated-period', null),

  SET_CALCULATOR_EARNINGS: defineMutation('calculator.earnings', 'calculator-earnings', null),

  SET_OFFER: defineMutation('offer', 'offer', null),
  SET_ORDER_ID: defineMutation('order.id', 'order-id', null),
  SET_ORDER_STATUS_ID: defineMutation('order.statusId', 'order-status-id', null),
  SET_IS_PRICE_APPROVED: defineMutation('order.isPriceApproved', 'is-price-approved', false),
  SET_ORDER_PAYMENTS(state, payments) {
    state.order.payments = payments;
  },
  SET_MTSBU_CODE(state, mtsbuCode) {
    state.order.mtsbuCode = mtsbuCode;
  },
  SET_MTSBU_LINK: defineMutation('order.mtsbuLink', 'mtsbu-link', null),
  SET_PAYMENT_INVOICE_ID: defineMutation('order.paymentInvoiceId', 'payment-invoice-id', null),
  SET_PAYMENT_SYSTEM_CODE: defineMutation('order.paymentSystemCode', 'payment-system-code', null),
  SET_OTP_FLAGS(state, flags) {
    if (flags === null || typeof flags !== 'object') return;

    if (flags.requiresOtp !== undefined) {
      state.otpFlags.requiresOtp = flags.requiresOtp;
      storage.set('otp-requires', flags.requiresOtp);
    }
    if (flags.otpType !== undefined) {
      state.otpFlags.otpType = flags.otpType;
      storage.set('otp-type', flags.otpType);
    }
    if (flags.otpSent !== undefined) {
      state.otpFlags.otpSent = flags.otpSent;
      storage.set('otp-sent', flags.otpSent);
    }
    if (flags.otpVerified !== undefined) {
      state.otpFlags.otpVerified = flags.otpVerified;
      storage.set('otp-verified', flags.otpVerified);
    }
  },
  SET_SANCTIONS(state, sanctions) {
    if (sanctions === null || typeof sanctions !== 'object') return;

    if (sanctions.isSanctioned !== undefined && typeof sanctions.isSanctioned === 'boolean') {
      state.sanctions.isSanctioned = sanctions.isSanctioned;
    }
    if (sanctions.sanctionMessage !== undefined) {
      state.sanctions.sanctionMessage = sanctions.sanctionMessage;
    }
    if (sanctions.status !== undefined && typeof sanctions.status === 'string') {
      state.sanctions.status = sanctions.status;
    }
  },
  CLEAR_ORDER_DATA(state) {
    state.order.id = null;
    state.order.statusId = null;
    state.order.isPriceApproved = false;
    state.order.payments = [];
    state.order.mtsbuCode = null;
    state.order.mtsbuLink = null;
    state.order.paymentInvoiceId = null;
    state.order.paymentSystemCode = null;
    state.otpFlags.requiresOtp = false;
    state.otpFlags.otpType = null;
    state.otpFlags.otpSent = false;
    state.otpFlags.otpVerified = false;
    state.sanctions.isSanctioned = false;
    state.sanctions.sanctionMessage = null;
    state.sanctions.status = null;

    storage.set('order-id', null);
    storage.set('order-status-id', null);
    storage.set('is-price-approved', false);
    storage.set('mtsbu-link', null);
    storage.set('payment-invoice-id', null);
    storage.set('payment-system-code', null);
    storage.set('otp-requires', false);
    storage.set('otp-type', null);
    storage.set('otp-sent', false);
    storage.set('otp-verified', false);
  },
  CLEAR_ALL(state) {
    state.pet.petType = null;
    state.pet.petBreed = null;
    state.pet.petSex = null;
    state.pet.petAlias = null;
    state.pet.petBirthDate = null;

    state.vetPassport.serial = null;
    state.vetPassport.number = null;
    state.vetPassport.date = null;
    state.vetPassport.issuedBy = null;

    state.microchip.code = null;
    state.microchip.date = null;

    state.policyholder.id = null;
    state.policyholder.type = 'natural';
    state.policyholder.fullName = null;
    state.policyholder.taxNumber = null;
    state.policyholder.withoutTaxNumber = false;
    state.policyholder.address = null;
    state.policyholder.email = null;
    state.policyholder.phone = null;
    state.policyholder.dateOfBirth = null;
    state.policyholder.documentsList = [];

    state.document.id = null;
    state.document.type = null;
    state.document.serialNumber = null;
    state.document.number = null;
    state.document.issuingAuthority = null;
    state.document.issueDate = null;
    state.document.expirationDate = null;

    state.contract.startDate = null;
    state.contract.estimatedPeriod = null;

    state.calculator.earnings = null;

    state.offer = null;

    state.order.id = null;
    state.order.statusId = null;
    state.order.isPriceApproved = false;
    state.order.payments = [];
    state.order.mtsbuCode = null;
    state.order.mtsbuLink = null;
    state.order.paymentInvoiceId = null;
    state.order.paymentSystemCode = null;
    state.otpFlags.requiresOtp = false;
    state.otpFlags.otpType = null;
    state.otpFlags.otpSent = false;
    state.otpFlags.otpVerified = false;
    state.sanctions.isSanctioned = false;
    state.sanctions.sanctionMessage = null;
    state.sanctions.status = null;

    storage.clear();
  },
};

const actions = {
  setPetType({ commit }, petType) {
    commit('SET_PET_TYPE', petType);
  },
  setPetBreed({ commit }, petBreed) {
    commit('SET_PET_BREED', petBreed);
  },
  setPetSex({ commit }, petSex) {
    commit('SET_PET_SEX', petSex);
  },
  setPetAlias({ commit }, petAlias) {
    commit('SET_PET_ALIAS', petAlias);
  },
  setPetBirthDate({ commit }, petBirthDate) {
    commit('SET_PET_BIRTH_DATE', petBirthDate);
  },

  setVetPassportSerial({ commit }, serial) {
    commit('SET_VET_PASSPORT_SERIAL', serial);
  },
  setVetPassportNumber({ commit }, number) {
    commit('SET_VET_PASSPORT_NUMBER', number);
  },
  setVetPassportDate({ commit }, date) {
    commit('SET_VET_PASSPORT_DATE', date);
  },
  setVetPassportIssuedBy({ commit }, issuedBy) {
    commit('SET_VET_PASSPORT_ISSUED_BY', issuedBy);
  },

  setMicrochipCode({ commit }, code) {
    commit('SET_MICROCHIP_CODE', code);
  },
  setMicrochipDate({ commit }, date) {
    commit('SET_MICROCHIP_DATE', date);
  },

  setPolicyholderId({ commit }, id) {
    commit('SET_POLICYHOLDER_ID', id);
  },
  setPolicyholderType({ commit }, type) {
    commit('SET_POLICYHOLDER_TYPE', type);
  },
  setPolicyholderFullName({ commit }, fullName) {
    commit('SET_POLICYHOLDER_FULL_NAME', fullName);
  },
  setPolicyholderTaxNumber({ commit }, taxNumber) {
    commit('SET_POLICYHOLDER_TAX_NUMBER', taxNumber);
  },
  setPolicyholderWithoutTaxNumber({ commit }, withoutTaxNumber) {
    commit('SET_POLICYHOLDER_WITHOUT_TAX_NUMBER', withoutTaxNumber);
  },
  setPolicyholderAddress({ commit }, address) {
    commit('SET_POLICYHOLDER_ADDRESS', address);
  },
  setPolicyholderEmail({ commit }, email) {
    commit('SET_POLICYHOLDER_EMAIL', email);
  },
  setPolicyholderPhone({ commit }, phone) {
    commit('SET_POLICYHOLDER_PHONE', phone);
  },
  setPolicyholderDateOfBirth({ commit }, dateOfBirth) {
    commit('SET_POLICYHOLDER_DATE_OF_BIRTH', dateOfBirth);
  },
  setPolicyholderDocumentsList({ commit }, documentsList) {
    commit('SET_POLICYHOLDER_DOCUMENTS_LIST', documentsList);
  },

  setDocumentId({ commit }, id) {
    commit('SET_DOCUMENT_ID', id);
  },
  setDocumentType({ commit }, type) {
    commit('SET_DOCUMENT_TYPE', type);
  },
  setDocumentSerialNumber({ commit }, serialNumber) {
    commit('SET_DOCUMENT_SERIAL_NUMBER', serialNumber);
  },
  setDocumentNumber({ commit }, number) {
    commit('SET_DOCUMENT_NUMBER', number);
  },
  setDocumentIssuingAuthority({ commit }, authority) {
    commit('SET_DOCUMENT_ISSUING_AUTHORITY', authority);
  },
  setDocumentIssueDate({ commit }, date) {
    commit('SET_DOCUMENT_ISSUE_DATE', date);
  },
  setDocumentExpirationDate({ commit }, date) {
    commit('SET_DOCUMENT_EXPIRATION_DATE', date);
  },

  setContractStartDate({ commit }, date) {
    commit('SET_CONTRACT_START_DATE', date);
  },
  setContractEstimatedPeriod({ commit }, period) {
    commit('SET_CONTRACT_ESTIMATED_PERIOD', period);
  },

  setCalculatorEarnings({ commit }, earnings) {
    commit('SET_CALCULATOR_EARNINGS', earnings);
  },
  async fetchTariffs({ commit, getters }) {
    try {
      const response = await tariffsPetsApi();
      const tariffs = (response?.data?.data || []).sort((a, b) => a.value - b.value);
      if (tariffs.length && !getters.getCalculatorEarnings) {
        commit('SET_CALCULATOR_EARNINGS', tariffs[0]?.value);
      }
      return tariffs;
    } catch (_) {
      return [];
    }
  },
  setOffer({ commit }, offer) {
    commit('SET_OFFER', offer);
  },

  setOrderId({ commit }, orderId) {
    commit('SET_ORDER_ID', orderId);
  },
  setOrderStatusId({ commit }, orderStatusId) {
    commit('SET_ORDER_STATUS_ID', orderStatusId);
  },
  setIsPriceApproved({ commit }, isPriceApproved) {
    commit('SET_IS_PRICE_APPROVED', isPriceApproved);
  },
  setOtpFlags({ commit }, flags) {
    commit('SET_OTP_FLAGS', flags);
  },
  setSanctions({ commit }, sanctions) {
    commit('SET_SANCTIONS', sanctions);
  },
  setOrderPayments({ commit }, payments) {
    commit('SET_ORDER_PAYMENTS', payments);
  },
  setPaymentInvoiceId({ commit }, invoiceId) {
    commit('SET_PAYMENT_INVOICE_ID', invoiceId);
  },
  setPaymentSystemCode({ commit }, code) {
    commit('SET_PAYMENT_SYSTEM_CODE', code);
  },

  async calculatePets({ commit }, params) {
    const response = await calculatePetsApi(params);
    commit('SET_OFFER', response?.data || null);
    return response?.data;
  },
  async saveOrder({ commit }, orderDraft) {
    try {
      const response = await savePetsOrderApi(orderDraft);
      const orderData = response?.data?.[0];

      if (orderData?.status === saveOrderResults.SANCTIONED || orderData?.status === saveOrderResults.POSSIBLE_MATCH) {
        commit('SET_SANCTIONS', {
          isSanctioned: true,
          sanctionMessage: orderData?.message || null,
          status: orderData?.status,
        });

        return { result: saveOrderResults.SANCTIONED };
      }

      const orderId = orderData?.id;

      if (orderId) {
        commit('SET_ORDER_ID', orderId);
        commit('SET_ORDER_STATUS_ID', orderDraft.statusId);
        commit('SET_OTP_FLAGS', {
          requiresOtp: true,
          otpType: orderData?.otpType ?? orderData?.otp_type ?? null,
        });
        const mtsbuLink = orderData?.mtsbuLink || null;
        const warningCancelMessage = orderData?.warningCancelMessage || null;

        if (mtsbuLink) {
          commit('SET_MTSBU_LINK', mtsbuLink);
          return { result: saveOrderResults.DECLARED_SUCCESS, orderId, mtsbuLink, warningCancelMessage };
        }

        return { result: saveOrderResults.SUCCESS, orderId };
      }

      if (response?.message) {
        const errorInfo = typeof response.message === 'object' ? (response.message.errorInfo || null) : null;
        return { result: saveOrderResults.RESPONSE_MESSAGE, message: response.message, errorInfo };
      }

      return { result: saveOrderResults.UNKNOWN };
    } catch (error) {
      const errorMessage = error?.response?.data?.message;

      if (errorMessage === BACKEND_ERRORS.BLOCKED_ORDERS) {
        return { result: saveOrderResults.BLOCKED };
      }

      if (errorMessage === BACKEND_ERRORS.BLACKLIST) {
        return { result: saveOrderResults.BLACKLIST };
      }

      if (errorMessage) {
        return { result: saveOrderResults.ERROR_MESSAGE, message: errorMessage };
      }

      return { result: saveOrderResults.UNKNOWN, error };
    }
  },
  async confirmContract(_, data) {
    try {
      return await confirmPetsContractApi(data);
    } catch (error) {
      const responseData = error?.response?.data;
      const insufficientFunds = responseData?.data?.orderId?.[0]?.includes(BACKEND_ERRORS.INSUFFICIENT_FUNDS);

      if (insufficientFunds) {
        return { error: confirmContractResults.INSUFFICIENT_FUNDS, message: responseData.data.orderId[0] };
      }

      if (responseData?.message) {
        return { error: confirmContractResults.RESPONSE_MESSAGE, message: responseData.message };
      }

      return { error: confirmContractResults.UNKNOWN };
    }
  },
  async takeContract(context, contractId) {
    try {
      return await takePetsContractApi(contractId);
    } catch (error) {
      const message = error?.response?.data?.message || 'Помилка отримання договору';
      throw new Error(message);
    }
  },
  clearOrderData({ commit }) {
    commit('CLEAR_ORDER_DATA');
  },

  async fetchOrderPayments({ commit, state, dispatch }, orderId = null) {
    const id = orderId || state.order.id;
    if (!id) return;

    try {
      const response = await fetchOrderApi(id);
      const orderData = Array.isArray(response) ? response[0] : response;
      const payments = orderData?.data?.payments || orderData?.payments || [];
      const mtsbuCode = orderData?.data?.mtsbuCode || orderData?.mtsbuCode || null;
      commit('SET_ORDER_PAYMENTS', payments);
      commit('SET_MTSBU_CODE', mtsbuCode);
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не вдалося завантажити дані про оплату';

      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error,
          title: 'Не вдалося завантажити дані про оплату',
          message: errorMessage,
        }, { root: true });
      }
    }
  },
  async checkPaymentStatus(_, invoiceId) {
    try {
      const result = await checkPaymentApi(invoiceId);
      return {
        statusId: result?.data?.invoice?.status_id,
        message: result?.message,
      };
    } catch (error) {
      const message = error?.response?.data?.message || 'Помилка перевірки статусу оплати';
      throw new Error(message);
    }
  },
  async sendOtp({ commit, state, dispatch }, { type, commissionPaidBy = null }) {
    try {
      const response = await sendOtpApi(state.order.id, type, commissionPaidBy);
      const status = response?.data?.message ?? null;

      if (status !== 'OK') {
        return { error: true };
      }

      commit('SET_OTP_FLAGS', { otpSent: true });

      if (response.data.data?.additional_otps) {
        return { additionalOtps: response.data.data.additional_otps };
      }

      return {};
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не вдалося відправити OTP код';

      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error,
          title: 'Не вдалося відправити OTP код',
          message: errorMessage,
        }, { root: true });
      }

      return { error: true };
    }
  },
  async checkOtp({ commit, state, dispatch }, { otp, additionalOtps = {} }) {
    try {
      const response = await checkOtpApi(state.order.id, otp, additionalOtps);
      const status = response?.data?.message ?? null;

      if (status !== 'OK') {
        return { error: 'invalid' };
      }

      commit('SET_OTP_FLAGS', { otpVerified: true, otpSent: true });
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не вдалося перевірити OTP код';

      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error,
          title: 'Не вдалося перевірити OTP код',
          message: errorMessage,
        }, { root: true });
      }

      return { error: 'invalid' };
    }
  },
  clear({ commit }) {
    commit('CLEAR_ALL');
  },
};

const getters = {
  getPetType: state => state.pet.petType,
  getPetBreed: state => state.pet.petBreed,
  getPetSex: state => state.pet.petSex,
  getPetAlias: state => state.pet.petAlias,
  getPetBirthDate: state => state.pet.petBirthDate,

  getVetPassportSerial: state => state.vetPassport.serial,
  getVetPassportNumber: state => state.vetPassport.number,
  getVetPassportDate: state => state.vetPassport.date,
  getVetPassportIssuedBy: state => state.vetPassport.issuedBy,

  getMicrochipCode: state => state.microchip.code,
  getMicrochipDate: state => state.microchip.date,

  getPolicyholderId: state => state.policyholder.id,
  getPolicyholderType: state => state.policyholder.type,
  getPolicyholderFullName: state => state.policyholder.fullName,
  getPolicyholderTaxNumber: state => state.policyholder.taxNumber,
  isPolicyholderWithoutTaxNumber: state => state.policyholder.withoutTaxNumber,
  getPolicyholderAddress: state => state.policyholder.address,
  getPolicyholderEmail: state => state.policyholder.email,
  getPolicyholderPhone: state => state.policyholder.phone,
  getPolicyholderDateOfBirth: state => state.policyholder.dateOfBirth,
  getPolicyholderDocumentsList: state => state.policyholder.documentsList,

  getDocumentId: state => state.document.id,
  getDocumentType: state => state.document.type,
  getDocumentSerialNumber: state => state.document.serialNumber,
  getDocumentNumber: state => state.document.number,
  getDocumentIssuingAuthority: state => state.document.issuingAuthority,
  getDocumentIssueDate: state => state.document.issueDate,
  getDocumentExpirationDate: state => state.document.expirationDate,

  getContractStartDate: state => state.contract.startDate,
  getContractEstimatedPeriod: state => state.contract.estimatedPeriod,

  getCalculatorEarnings: state => state.calculator.earnings,

  getOffer: state => state.offer,

  getOrderId: state => state.order.id,
  getOrderStatusId: state => state.order.statusId,
  getIsPriceApproved: state => state.order.isPriceApproved,
  getPayments: state => state.order.payments,
  getMtsbuCode: state => state.order.mtsbuCode,
  getMtsbuLink: state => state.order.mtsbuLink,
  getPaymentInvoiceId: state => state.order.paymentInvoiceId,
  getPaymentSystemCode: state => state.order.paymentSystemCode,
  getOtpFlags: state => state.otpFlags,
  getSanctions: state => state.sanctions,
  requiresOtp: state => state.otpFlags.requiresOtp,
  isSanctioned: state => state.sanctions.isSanctioned,
  sanctionMessage: state => state.sanctions.sanctionMessage,
  getIsPaid: (state) => {
    const payments = state.order.payments;
    if (!payments?.length) return false;

    const basePrice = Number(state.offer?.pricing?.basePrice) || 0;

    const sumPay = payments.reduce((sum, item) => {
      if (item.invoice?.status_id === invoiceStatuses.PAID) {
        return sum + parseFloat(item.invoice.amount);
      }
      return sum;
    }, 0);

    return basePrice > 0 && sumPay >= basePrice;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
